import { useState, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
  IconButton,
  Popover,
  MenuItem,
} from '@mui/material';

import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import TeamDialog from '../components/contest/TeamDialog';

import { uploadFileWithName } from 'src/api/files/uploadFiles';
import DeleteDialog from 'src/components/pages/DeleteDialog';
import { env } from '../config/env';
import { TeamType } from 'src/types/domain.types';
import { useGetTeamsQuery } from '../api/teams/getTeams';
import { useCreateTeamMutation } from '../api/teams/createTeam';
import { useUpdateTeamMutation } from '../api/teams/updateTeam';
import { useDeleteTeamMutation } from '../api/teams/deleteTeam';
import { CompetitionFormControl } from '../components/form/CompetitionFormControl';
import { TableHeaderType } from '../types/custom.types';

export default function TeamsPage() {
  const [openCreateTeam, setOpenCreateTeam] = useState(false);
  const [openPopover, setOpenPopover] = useState(null);
  const [openEdit, setOpenEdit] = useState<TeamType | null>(null);
  const [editTeam, setEditTeam] = useState<TeamType | null>(null);
  const [openDelete, setOpenDelete] = useState<TeamType | null>(null);
  const [competitionId, setCompetitionId] = useState<number>(1); // filter by competitionId, default ID = 1
  const fileInputRef = useRef(null);

  const { data: teams = [], isLoading, refetch } = useGetTeamsQuery();
  const { mutate: createTeam } = useCreateTeamMutation({
    onSuccess: () => {
      setOpenCreateTeam(false);
      refetch();
    },
    onError: (error) => {
      Promise.reject(error);
    },
  });

  const { mutate: updateTeam } = useUpdateTeamMutation({
    onSuccess: () => {
      setOpenEdit(null);
      refetch();
    },
    onError: (error) => {
      Promise.reject(error);
    },
  });

  const { mutate: deleteTeam } = useDeleteTeamMutation({
    onSuccess: () => {
      refetch();
      setOpenDelete(null);
    },
    onError: (error) => {
      Promise.reject(error);
    },
  });

  const handleCreateTeam = (team: TeamType) => {
    createTeam(team);
  };

  const handleUpdateTeam = (team: TeamType) => {
    updateTeam(team);
  };

  const handleDeleteTeam = (teamId: number) => {
    deleteTeam(teamId);
  };

  const handleOpenMenu = (team: TeamType) => (event: any) => {
    setOpenPopover(event.currentTarget);
    setEditTeam(team);
  };

  const handleCloseMenu = () => {
    setOpenPopover(null);
    setEditTeam(null);
  };

  const openEditTeam = () => {
    if (editTeam) {
      setOpenPopover(null);
      setEditTeam(editTeam);
      setOpenEdit(editTeam);
    }
  };

  const openDeleteTeam = () => {
    if (editTeam) {
      setOpenPopover(null);
      setEditTeam(editTeam);
      setOpenDelete(editTeam);
    }
  };

  const handleCloseDialog = () => {
    setOpenEdit(null);
    setOpenCreateTeam(false);
    setEditTeam(null);
  };

  const handleChangeLogo = () => (fileInputRef.current as any)?.click();

  const uploadImage = async (event: any) => {
    setOpenPopover(null);

    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      const fileName = `${editTeam?.team_feed_id}.png`;

      try {
        await uploadFileWithName(file, fileName);
        setEditTeam(null);
      } catch (error) {
        console.error('Upload error:', error);
      }
    }
  };

  const tableHeader: TableHeaderType[] = [
    { id: 'team_id', label: 'ID' },
    { id: 'name_en', label: 'Name' },
    { id: 'team_feed_id', label: 'Team Feed ID' },
    { id: 'logo', label: 'Logo' },
    { id: 'actions', label: 'Actions', align: 'right' },
  ];

  const teamsByCompetitionId = useMemo(() => {
    return teams.filter(
      (team: TeamType) => team.competition_id === competitionId
    );
  }, [teams, competitionId]);

  return (
    <>
      <Helmet>
        <title>Teams</title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            Teams
          </Typography>
          <Button
            variant='contained'
            startIcon={<Iconify icon='eva:plus-fill' />}
            onClick={() => setOpenCreateTeam(true)}
          >
            New Team
          </Button>
        </Stack>

        <Box sx={{ my: 1 }}>
          <CompetitionFormControl
            value={competitionId}
            onChange={(event: any) => setCompetitionId(event.target.value)}
          />
        </Box>

        <Card>
          {isLoading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center' }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      {tableHeader.map(({ id, label, align = 'left' }) => (
                        <TableCell key={id} align={align}>
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamsByCompetitionId.map((team) => (
                      <TableRow
                        key={team.team_id}
                        sx={{
                          '&:last-child td, &:last-child th': {
                            border: 0,
                          },
                        }}
                      >
                        <TableCell>{team.team_id}</TableCell>
                        <TableCell>{team.name_en}</TableCell>
                        <TableCell>{team.team_feed_id}</TableCell>
                        <TableCell>
                          <img
                            alt={`${team.name_en}-logo`}
                            src={`https://${env.STORAGERESOURCENAME}.blob.core.windows.net/${env.STORAGE_CONTAINER}/${team.team_feed_id}.png`}
                            style={{
                              width: '32px',
                              maxHeight: '32px',
                              objectFit: 'contain',
                            }}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <IconButton
                            size='large'
                            color='inherit'
                            onClick={handleOpenMenu(team)}
                          >
                            <Iconify icon={'eva:more-vertical-fill'} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </Card>
      </Container>

      <Popover
        open={Boolean(openPopover)}
        anchorEl={openPopover}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              p: 1,
              width: 152,
              '& .MuiMenuItem-root': {
                px: 1,
                typography: 'body2',
                borderRadius: 0.75,
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleChangeLogo}>
          <Iconify icon={'eva:image-outline'} sx={{ mr: 2 }} />
          Change Logo
          <input
            accept='image/*'
            hidden
            multiple
            onChange={uploadImage}
            type='file'
            ref={fileInputRef}
          />
        </MenuItem>
        <MenuItem onClick={openEditTeam}>
          <Iconify icon={'eva:edit-outline'} sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => openDeleteTeam()}>
          <Iconify icon={'eva:trash-outline'} sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Popover>

      {openCreateTeam && (
        <TeamDialog onSave={handleCreateTeam} onClose={handleCloseDialog} />
      )}

      {openEdit && (
        <TeamDialog
          team={editTeam}
          onSave={handleUpdateTeam}
          onClose={handleCloseDialog}
        />
      )}
      {openDelete && (
        <DeleteDialog
          openConfirm={Boolean(openDelete)}
          type={`team "${editTeam?.name_en}"`}
          id={Number(editTeam?.team_id)}
          deleteHandler={() => handleDeleteTeam(Number(editTeam?.team_id))}
          setOpenConfirm={handleCloseDialog}
        />
      )}
    </>
  );
}
